/* eslint-disable max-lines */
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import MuiTextField from '@mui/material/TextField';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import RefreshIcon from '@mui/icons-material/Refresh';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from 'common/forms/TextField';
import { isEmpty, pick } from 'lodash';
import { Field, Form } from 'react-final-form';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import {
  validateGreaterThanOrEqual,
  validateQuantity,
  validateRefills,
} from 'common/forms/formValidations';
import { unMatchedFields } from 'common/utils';
import { PrescriptionReviewPayloadContext } from 'common/hooks/usePrescriptionReviewPayloadContext';
import { listPracticeProvidersApi } from 'api/practice';
import VerifiedTag from 'common/modules/VerifiedTag';
import { VerificationStatus } from 'enums/provider.enum';
import { ProviderVerificationStatus } from 'enums/common.enum';
import AddPracticeProviderDialog from './AddPracticeProviderDialog';

const formatProviderName = (provider) =>
  `${provider?.firstName || ''} ${provider?.middleName || ''} ${provider?.lastName || ''}`
    .replace(/\s+/g, ' ')
    .trim();

// The practice's providers come back in API order; the picker offers them A–Z by the name it shows.
const compareByProviderName = (left, right) =>
  formatProviderName(left).localeCompare(formatProviderName(right), undefined, {
    sensitivity: 'base',
  });

const EditPrescriptionModal = ({
  mpi,
  open,
  onClose,
  editablePrescriptionDetails,
  currentRefill,
  practiceId,
}) => {
  const {
    payload,
    savePrescriptionChanges,
    undoPrescription,
    redoPrescription,
    resetPrescriptionChanges,
    canUndoPrescription,
    canRedoPrescription,
  } = useContext(PrescriptionReviewPayloadContext);

  const formRef = useRef(null);
  const prevPayloadRef = useRef(null);
  const [isEditingPrescriber, setIsEditingPrescriber] = useState(false);
  const [providers, setProviders] = useState(null);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [showAddProviderDialog, setShowAddProviderDialog] = useState(false);

  const getProviderAutocompleteValue = (providersList, formApi, currentInputValue) => {
    if (!providersList) return null;
    const formValues = formApi.getState().values || {};
    const selectedById = formValues.providerId
      ? providersList.find((provider) => provider.providerId === formValues.providerId)
      : null;
    if (selectedById) return selectedById;
    return (
      providersList.find((provider) => formatProviderName(provider) === currentInputValue) || null
    );
  };

  const initialValues = useMemo(() => {
    const base = isEmpty(payload?.prescriptionDetails)
      ? editablePrescriptionDetails
      : { ...editablePrescriptionDetails, ...payload.prescriptionDetails };
    return base;
  }, [editablePrescriptionDetails, payload?.prescriptionDetails]);

  const closeModal = () => {
    setIsEditingPrescriber(false);
    onClose();
  };

  const loadProviders = useCallback(() => {
    if (practiceId) {
      setLoadingProviders(true);
      listPracticeProvidersApi({ practiceId, limit: 0, offset: 0 })
        .then(({ result }) => setProviders([...(result || [])].sort(compareByProviderName)))
        .finally(() => setLoadingProviders(false));
    }
  }, [practiceId]);

  const handleProviderAdded = () => {
    loadProviders();
  };

  useEffect(() => {
    loadProviders();
  }, [loadProviders]);

  useEffect(() => {
    if (open && payload?.prescriptionDetails) {
      prevPayloadRef.current = JSON.parse(JSON.stringify(payload.prescriptionDetails));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || !formRef.current) {
      return;
    }

    const currentPayload = payload?.prescriptionDetails || {};
    const prevPayload = prevPayloadRef.current || {};

    const currentPrescriptionFields = {
      prescriberName: currentPayload.prescriberName,
      refills: currentPayload.refills,
      prescribedQuantity: currentPayload.prescribedQuantity,
      providerId: currentPayload.providerId,
    };
    const prevPrescriptionFields = {
      prescriberName: prevPayload.prescriberName,
      refills: prevPayload.refills,
      prescribedQuantity: prevPayload.prescribedQuantity,
      providerId: prevPayload.providerId,
    };

    const fieldsChanged =
      JSON.stringify(prevPrescriptionFields) !== JSON.stringify(currentPrescriptionFields);

    if (fieldsChanged && prevPayload && Object.keys(prevPayload).length > 0) {
      if (currentPrescriptionFields.prescriberName !== undefined) {
        formRef.current.change('prescriberName', currentPrescriptionFields.prescriberName);
      } else if (prevPrescriptionFields.prescriberName !== undefined) {
        formRef.current.change('prescriberName', editablePrescriptionDetails.prescriberName);
      }

      if (currentPrescriptionFields.refills !== undefined) {
        formRef.current.change('refills', currentPrescriptionFields.refills);
      } else if (prevPrescriptionFields.refills !== undefined) {
        formRef.current.change('refills', editablePrescriptionDetails.refills);
      }

      if (currentPrescriptionFields.prescribedQuantity !== undefined) {
        formRef.current.change('prescribedQuantity', currentPrescriptionFields.prescribedQuantity);
      } else if (prevPrescriptionFields.prescribedQuantity !== undefined) {
        formRef.current.change(
          'prescribedQuantity',
          editablePrescriptionDetails.prescribedQuantity
        );
      }

      if (currentPrescriptionFields.providerId !== undefined) {
        formRef.current.change('providerId', currentPrescriptionFields.providerId);
      } else if (prevPrescriptionFields.providerId !== undefined) {
        formRef.current.change('providerId', editablePrescriptionDetails?.providerId);
      }

      prevPayloadRef.current = JSON.parse(JSON.stringify(currentPrescriptionFields));
    } else if (!prevPayload || Object.keys(prevPayload).length === 0) {
      prevPayloadRef.current = JSON.parse(JSON.stringify(currentPrescriptionFields));
    }
  }, [payload?.prescriptionDetails, open, editablePrescriptionDetails]);

  const handleReset = () => {
    resetPrescriptionChanges();
    if (formRef.current) {
      formRef.current.change('prescriberName', editablePrescriptionDetails.prescriberName);
      formRef.current.change('refills', editablePrescriptionDetails.refills);
      formRef.current.change('prescribedQuantity', editablePrescriptionDetails.prescribedQuantity);
      formRef.current.change('providerId', editablePrescriptionDetails?.providerId);
    }
  };

  const handleResolvePrescription = (formData) => {
    const overRidePrescriptionDetails = unMatchedFields(editablePrescriptionDetails, formData);
    const newPrescriptionDetails = pick(formData, overRidePrescriptionDetails);

    if (formData.providerId) {
      newPrescriptionDetails.providerId = formData.providerId;
    }

    savePrescriptionChanges(newPrescriptionDetails);
    setIsEditingPrescriber(false);

    onClose();
  };

  return (
    <>
      <Dialog open={open} maxWidth='sm' fullWidth>
        <DialogTitle>Edit Prescription</DialogTitle>

        <Form
          initialValues={initialValues}
          onSubmit={handleResolvePrescription}
          render={({ handleSubmit, invalid, dirtyFields, form }) => {
            if (formRef.current !== form) {
              formRef.current = form;
            }

            return (
              <form noValidate onSubmit={handleSubmit}>
                <DialogContent>
                  <Typography variant='h6' gutterBottom sx={{ mt: -2, mb: 1, fontWeight: 600 }}>
                    Change Prescription Details
                  </Typography>

                  {isEditingPrescriber ? (
                    <Box sx={{ my: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Field name='prescriberName'>
                        {({ input }) => (
                          <Autocomplete
                            sx={{ flex: 1 }}
                            fullWidth
                            options={providers || []}
                            loading={loadingProviders}
                            value={getProviderAutocompleteValue(providers, form, input.value)}
                            onChange={(_, provider) => {
                              input.onChange(provider ? formatProviderName(provider) : '');
                              form.change('providerId', provider ? provider.providerId : undefined);
                            }}
                            getOptionLabel={(option) => formatProviderName(option)}
                            isOptionEqualToValue={(option, value) => option === value}
                            noOptionsText={
                              <Typography sx={{ fontSize: '1em' }}>No providers found.</Typography>
                            }
                            renderOption={(props, option) => (
                              // eslint-disable-next-line react/jsx-props-no-spreading
                              <li {...props} key={option.providerId}>
                                <Box sx={{ width: '100%' }}>
                                  <Typography sx={{ fontWeight: 500 }}>
                                    {formatProviderName(option)}
                                  </Typography>
                                  {option.npi && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Typography
                                        variant='body2'
                                        sx={{
                                          fontSize: '0.85rem',
                                          color: (theme) => theme.palette.text.secondary,
                                        }}
                                      >
                                        NPI: {option.npi}
                                      </Typography>
                                      <Box
                                        sx={{
                                          display: 'inline-flex',
                                          '& svg': { fontSize: '1.2rem' },
                                        }}
                                      >
                                        <VerifiedTag
                                          status={
                                            ProviderVerificationStatus[option.verificationStatus] ||
                                            VerificationStatus.UNVERIFIED
                                          }
                                        />
                                      </Box>
                                    </Box>
                                  )}
                                </Box>
                              </li>
                            )}
                            renderInput={(params) => (
                              <MuiTextField
                                // eslint-disable-next-line react/jsx-props-no-spreading
                                {...params}
                                label='Search Prescriber by Name'
                                InputProps={{
                                  // eslint-disable-next-line react/jsx-props-no-spreading
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {loadingProviders ? (
                                        <CircularProgress color='inherit' size={20} />
                                      ) : null}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                          />
                        )}
                      </Field>
                      <Tooltip title='Add New Provider'>
                        <IconButton
                          size='small'
                          onClick={() => setShowAddProviderDialog(true)}
                          aria-label='Add New Provider'
                          color='primary'
                        >
                          <AddIcon fontSize='small' />
                        </IconButton>
                      </Tooltip>
                      <Button
                        size='small'
                        onClick={() => {
                          form.change('prescriberName', editablePrescriptionDetails.prescriberName);
                          form.change('providerId', editablePrescriptionDetails?.providerId);
                          setIsEditingPrescriber(false);
                        }}
                      >
                        Cancel Edit
                      </Button>
                    </Box>
                  ) : (
                    <TextField
                      id='EditPrescriptionModal-prescriber-name-input'
                      label='Prescriber Name'
                      name='prescriberName'
                      required
                      disabled
                      readOnly
                      endAdornment={
                        <Tooltip title='Edit Prescriber'>
                          <IconButton
                            size='small'
                            onClick={() => setIsEditingPrescriber(true)}
                            aria-label='Edit Prescriber'
                          >
                            <EditIcon fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      }
                    />
                  )}

                  <TextField
                    id='EditPrescriptionModal-quantity-input'
                    label='Prescribed Quantity'
                    name='prescribedQuantity'
                    required
                    validations={[
                      validateQuantity(),
                      validateGreaterThanOrEqual(
                        'Prescribed quantity cannot be less than the current fill quantity',
                        Number(editablePrescriptionDetails.dispensedQuantity) || 0
                      ),
                    ]}
                  />

                  <TextField
                    id='EditFillModal-refill-input'
                    label='Refills'
                    name='refills'
                    type='number'
                    required
                    validations={[
                      validateRefills('Should be higher than the current refill', currentRefill),
                    ]}
                  />
                </DialogContent>

                <DialogActions>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mr: 'auto' }}>
                    {(canUndoPrescription || canRedoPrescription) && (
                      <>
                        <Tooltip title='Undo prescription changes' arrow>
                          <span>
                            <IconButton
                              size='small'
                              onClick={undoPrescription}
                              disabled={!canUndoPrescription}
                              aria-label='Undo prescription changes'
                            >
                              <UndoIcon fontSize='small' />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title='Redo prescription changes' arrow>
                          <span>
                            <IconButton
                              size='small'
                              onClick={redoPrescription}
                              disabled={!canRedoPrescription}
                              aria-label='Redo prescription changes'
                            >
                              <RedoIcon fontSize='small' />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title='Reset to initial state' arrow>
                          <span>
                            <IconButton
                              size='small'
                              onClick={handleReset}
                              aria-label='Reset prescription changes'
                            >
                              <RefreshIcon fontSize='small' />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </>
                    )}
                  </Box>
                  <Button variant='outlined' color='secondary' onClick={closeModal}>
                    Cancel
                  </Button>
                  <LoadingButton
                    type='submit'
                    variant='contained'
                    disabled={isEmpty(dirtyFields) || invalid}
                  >
                    Confirm
                  </LoadingButton>
                </DialogActions>
              </form>
            );
          }}
        />
      </Dialog>
      <AddPracticeProviderDialog
        mpi={mpi}
        open={showAddProviderDialog}
        onClose={() => setShowAddProviderDialog(false)}
        practiceId={practiceId}
        onProviderAdded={handleProviderAdded}
      />
    </>
  );
};

EditPrescriptionModal.propTypes = {
  mpi: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  editablePrescriptionDetails: PropTypes.shape({
    refills: PropTypes.string,
    quantity: PropTypes.string,
    prescriberName: PropTypes.string,
    prescribedQuantity: PropTypes.string,
    dispensedQuantity: PropTypes.string,
    providerId: PropTypes.string,
  }).isRequired,
  currentRefill: PropTypes.string.isRequired,
  practiceId: PropTypes.string.isRequired,
};

export default EditPrescriptionModal;
